/**
 * Scheduling's inbox for expedited-dispense requests (workforce write
 * cutover, packet §16). Finance asked; Scheduling reviews, accepts or
 * declines, schedules, closes. Each answer carries the request's version so
 * two people cannot answer the same request twice without noticing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { schedulingApi, outcomeOf, shortDate, EXPEDITED_STATE_LABEL, SchedulingScope, InboxItem, SchedulingAction, ExpeditedState } from '../finance/financeApi';
import FacilityOperations from './FacilityOperations';
import SchedulingBoard from './SchedulingBoard';
import '../umed/umed.css';
import '../finance/finance.css';
import './scheduling.css';

/**
 * Views (Tide phase 2, 2026-10-04): the Scheduling BOARD - the Monday board as
 * a read model, tabs from the server's catalog, each building's plan and
 * commands under its row - is the default; the expedited inbox is the second.
 * The facility table (every building, not only the board's) stays reachable
 * at `?view=facilities`, linked from the board's foot, for scheduling a
 * building the board does not carry. The view rides in the URL so a link and
 * a refresh land where the person was.
 */
type View = 'board' | 'expedited' | 'facilities';
const isView = (v: string | null): v is View => v === 'board' || v === 'facilities' || v === 'expedited';
const TITLE: Record<View, string> = { board: 'Scheduling board', expedited: 'Expedited dispense requests', facilities: 'Facility operations' };

const ACTION_LABEL: Record<SchedulingAction, string> = { REVIEW: 'Start review', ACCEPT: 'Accept', DECLINE: 'Decline', SCHEDULE: 'Schedule the visit', CLOSE: 'Close' };
const ACTIONS_FOR: Record<ExpeditedState, SchedulingAction[]> = {
  REQUESTED: ['REVIEW', 'ACCEPT', 'DECLINE'], UNDER_REVIEW: ['ACCEPT', 'DECLINE'], ACCEPTED: ['SCHEDULE', 'CLOSE'], DECLINED: ['CLOSE'], SCHEDULED: ['CLOSE'], CLOSED: [],
};

function RequestCard({ item, canRespond, onDone }: { item: InboxItem; canRespond: boolean; onDone: (msg: string) => Promise<void> }) {
  const [action, setAction] = useState<SchedulingAction | null>(null);
  const [response, setResponse] = useState('');
  const [date, setDate] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const actions = ACTIONS_FOR[item.state];
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); if (!action) return; setBusy(true); setErr(null);
    try {
      const r = await schedulingApi.respond(item.id, { action, response: response.trim() || undefined, scheduled_for: date || undefined, visit_ref: ref.trim() || undefined, version: item.version });
      if (!r.success || !r.data) { setErr(outcomeOf(r, { wrote: true })); return; }
      await onDone(`${item.resident_name}: ${EXPEDITED_STATE_LABEL[r.data.state]}.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <li className="fn-request" aria-label="Expedited dispense request">
      <strong>{item.resident_name}</strong> · {item.facility_name || 'No current building'}
      <div className="um-quiet">{EXPEDITED_STATE_LABEL[item.state]} · asked {shortDate(item.requested_at)}{item.requested_by_name ? ` by ${item.requested_by_name}` : ''}{item.urgency === 'REQUESTED_BY_DATE' && item.requested_by_date ? ` · wanted by ${shortDate(item.requested_by_date)}` : ' · as soon as possible'}</div>
      <p>{item.reason}</p>
      {item.arrangement_summary && <p className="um-quiet">Arrangement: {item.arrangement_summary}</p>}
      <p className="um-quiet">Next visit to this building: {item.provider_next_visit_at ? shortDate(item.provider_next_visit_at) : item.tentative_next_visit ? `${shortDate(item.tentative_next_visit)} (tentative)` : 'not scheduled'}</p>
      {item.scheduling_response && <p className="um-quiet">Answer so far: {item.scheduling_response}</p>}
      {canRespond && actions.length > 0 && (
        <form className="um-form" onSubmit={submit} aria-label={`Answer for ${item.resident_name}`}>
          <div className="btns">{actions.map((a) => <button key={a} type="button" className={`um-open${action === a ? ' um-primary' : ''}`} onClick={() => setAction(a)}>{ACTION_LABEL[a]}</button>)}</div>
          {action && (
            <>
              <label><span>{action === 'DECLINE' ? 'Why (Finance reads this)' : 'Note for Finance (optional)'}</span><input value={response} onChange={(e) => setResponse(e.target.value)} maxLength={2000} aria-label="Scheduling response" /></label>
              {action === 'SCHEDULE' && <><label><span>Visit date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Visit date" /></label>
                <label><span>Visit reference (optional)</span><input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={200} aria-label="Visit reference" /></label></>}
              <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || (action === 'DECLINE' && !response.trim()) || (action === 'SCHEDULE' && !date)}>{busy ? 'Recording…' : `Confirm: ${ACTION_LABEL[action]}`}</button></div>
            </>
          )}
          {err && <p className="um-notice stop" role="alert">{err}</p>}
        </form>
      )}
    </li>
  );
}

export default function SchedulingInbox() {
  const [scope, setScope] = useState<SchedulingScope | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [state, setState] = useState<ExpeditedState | ''>('');
  const [listErr, setListErr] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = isView(searchParams.get('view')) ? (searchParams.get('view') as View) : 'board';
  // A view change starts that view clean: the board's tab, filters and open row belong to the board.
  const setView = (v: View) => { const next = new URLSearchParams(); if (v !== 'board') next.set('view', v); setSearchParams(next, { replace: true }); setNotice(null); };

  const load = useCallback(async () => {
    setListErr(null);
    const r = await schedulingApi.inbox(state || null);
    if (r.success && r.data) { setItems(r.data.items); return; }
    // Never `[]` on a failure. An empty array renders "Nothing waiting", which
    // tells Scheduling the queue is clear when the truth is that nobody
    // answered — and expedited dispense is the queue where that matters most.
    setItems(null);
    setListErr(outcomeOf(r));
  }, [state]);

  useEffect(() => {
    (async () => {
      try {
        const r = await schedulingApi.scope();
        if (!r.success || !r.data) { setRefusal(r.code === 'DENY_NO_CAPABILITY' ? 'Your role does not answer expedited-dispense requests. Ask your administrator to ratify a Scheduling role.' : outcomeOf(r)); return; }
        setScope(r.data);
      } catch { setRefusal('Could not reach the server.'); }
    })();
  }, []);
  useEffect(() => { if (scope) load(); }, [scope, load]);

  if (refusal) return <div className="um"><main className="um-main one"><section className="um-card"><h2>Scheduling</h2><p className="um-quiet">{refusal}</p></section></main></div>;
  if (!scope) return <div className="um"><main className="um-main one"><section className="um-card"><p className="um-quiet">Opening Scheduling…</p></section></main></div>;
  return (
    <div className="um board-host">
      <WorkspaceMenu current="SCHEDULING" />
      <header className="um-bar"><div className="um-bar-inner"><div className="um-ident"><div className="um-where">
        <span className="um-eyebrow">eMerge · Scheduling</span><h1>{TITLE[view]}</h1>
        <span className="um-sub">{view === 'board'
          ? `Every building's schedule in eMerge: its state, visit plan and coverage, with Monday's columns kept only as reference. ${scope.can.plan ? 'Plan, confirm and complete visits from an open row.' : 'Read-only: the visits write is not open for this account.'}`
          : view === 'facilities'
            ? `Every building, its last and next visit, who covers it, and what blocks it. ${scope.can.plan ? 'Plan, confirm and complete visits here.' : 'Read-only: the visits write is not open for this account.'}`
            : `Finance asks; Scheduling decides. ${scope.writes === 'NONE' ? 'Read-only on this process.' : ''}`}</span>
      </div></div></div></header>
      <main className={`um-main one${view === 'board' ? ' board-main' : ''}`}>
        <nav className="sc-views" aria-label="Scheduling views">
          <button type="button" className="um-open" aria-pressed={view === 'board'} onClick={() => setView('board')}>Board{scope.counts && scope.counts.open_blockers ? ` · ${scope.counts.open_blockers} blocked` : ''}</button>
          <button type="button" className="um-open" aria-pressed={view === 'expedited'} onClick={() => setView('expedited')}>Expedited requests{scope.counts && scope.counts.expedited_pending ? ` · ${scope.counts.expedited_pending}` : ''}</button>
          {view === 'facilities' && <button type="button" className="um-open" aria-pressed="true" onClick={() => setView('facilities')}>Facility table</button>}
        </nav>
        {notice && (view === 'facilities' || view === 'board') && <p className="um-notice ok" role="status">{notice}</p>}
        {view === 'board' && <SchedulingBoard scope={scope} onNotice={setNotice} onOpenFacilities={() => setView('facilities')} />}
        {view === 'facilities' && <FacilityOperations scope={scope} onNotice={setNotice} />}
        {view === 'expedited' && <div className="um-list">
          <label className="um-quiet">Show{' '}
            <select value={state} onChange={(e) => setState(e.target.value as ExpeditedState | '')} aria-label="Request state">
              <option value="">Open (requested, under review, accepted)</option>
              {scope.states.map((s) => <option key={s} value={s}>{EXPEDITED_STATE_LABEL[s]}</option>)}
            </select>
          </label>
          {notice && <p className="um-notice ok" role="status">{notice}</p>}
          {listErr && (
            <p className="um-notice stop" role="alert">
              {listErr}{' '}
              <button type="button" className="um-open" onClick={load}>Try again</button>
            </p>
          )}
          {!listErr && items === null && <p className="um-quiet">Loading…</p>}
          {!listErr && items && items.length === 0 && <p className="um-quiet">Nothing waiting.</p>}
          {items && items.length > 0 && <ul className="um-packets" aria-label="Requests">{items.map((it) => <RequestCard key={`${it.id}:${it.version}`} item={it} canRespond={scope.can.respond} onDone={async (m) => { setNotice(m); await load(); }} />)}</ul>}
        </div>}
      </main>
    </div>
  );
}
